import React, { useState, useRef, useCallback } from 'react';
import { Upload, File, Download, Trash2 } from 'lucide-react';
import { EvidenciaArquivo } from '../types';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  CategoriaArquivo,
  EXTENSOES_PERMITIDAS,
  TAMANHO_MAXIMO_MB,
  rotuloExtensoes,
  validarArquivo,
} from '../utils/arquivos';

interface FileUploadProps {
  gmudId: number;
  // 'evidencia' (padrão) ou 'script_banco' (anexos do script de banco de dados)
  categoria?: CategoriaArquivo;
  onFilesUploaded?: (files: EvidenciaArquivo[]) => void;
  // Disparado sempre que a lista de arquivos da GMUD é (re)carregada
  onFilesChange?: (files: EvidenciaArquivo[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  gmudId,
  categoria = 'evidencia',
  onFilesUploaded,
  onFilesChange,
}) => {
  const extensoes = EXTENSOES_PERMITIDAS[categoria];
  const [files, setFiles] = useState<EvidenciaArquivo[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onFilesChangeRef = useRef(onFilesChange);
  onFilesChangeRef.current = onFilesChange;

  const fetchFiles = useCallback(async () => {
    try {
      const response = await api.get(`/upload/evidencias/${gmudId}`, { params: { categoria } });
      setFiles(response.data);
      onFilesChangeRef.current?.(response.data);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
    }
  }, [gmudId, categoria]);

  // Carregar arquivos existentes
  React.useEffect(() => {
    fetchFiles();
  }, [gmudId, fetchFiles]);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);

    // Validar arquivos (por extensão: o tipo MIME informado pelo navegador varia por sistema)
    const validFiles = fileArray.filter(file => {
      const erro = validarArquivo(file, extensoes);
      if (erro) {
        toast.error(erro);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    uploadFiles(validFiles);
  };

  const uploadFiles = async (filesToUpload: File[]) => {
    setUploading(true);

    try {
      const formData = new FormData();
      filesToUpload.forEach(file => {
        formData.append('evidencia_arquivos', file);
      });

      const response = await api.post(`/upload/evidencias/${gmudId}`, formData, {
        params: { categoria },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${response.data.files.length} arquivo(s) enviado(s) com sucesso`);
      await fetchFiles(); // Recarregar lista de arquivos
      onFilesUploaded?.(files);
    } catch (error: unknown) {
      console.error('Erro no upload:', error);
      const errorMessage =
        (error as any)?.response?.data?.error || 'Erro ao enviar arquivos';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (fileId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este arquivo?')) return;

    try {
      await api.delete(`/upload/evidencias/${gmudId}/${fileId}`);
      toast.success('Arquivo deletado com sucesso');
      await fetchFiles(); // Recarregar lista de arquivos
    } catch (error: unknown) {
      console.error('Erro ao deletar arquivo:', error);
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.error
          : 'Erro ao deletar arquivo';
      toast.error(errorMessage);
    }
  };

  const downloadFile = async (fileId: number, fileName: string) => {
    try {
      const response = await api.get(`/upload/download/${gmudId}/${fileId}`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: unknown) {
      console.error('Erro no download:', error);
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.error
          : 'Erro ao baixar arquivo';
      toast.error(errorMessage);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  return (
    <div className='space-y-4'>
      {/* Área de Upload */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
          ${uploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={extensoes.join(',')}
          onChange={e => handleFileSelect(e.target.files)}
          className='hidden'
        />

        <Upload className='h-8 w-8 mx-auto mb-2 text-gray-400' />
        <p className='text-sm text-gray-600 mb-1'>
          {uploading ? 'Enviando arquivos...' : 'Arraste arquivos aqui ou clique para selecionar'}
        </p>
        <p className='text-xs text-gray-500'>
          {rotuloExtensoes(extensoes)} (máx. {TAMANHO_MAXIMO_MB}MB cada)
        </p>
      </div>

      {/* Lista de Arquivos */}
      {files.length > 0 && (
        <div className='space-y-2'>
          <h4 className='text-sm font-medium text-gray-700'>Arquivos Enviados</h4>
          <div className='space-y-2'>
            {files.map(file => (
              <div
                key={file.id}
                className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'
              >
                <div className='flex items-center space-x-3'>
                  <File className='h-5 w-5 text-gray-400' />
                  <div>
                    <p className='text-sm font-medium text-gray-900'>
                      {file.nome_original || file.nome_arquivo}
                    </p>
                    <p className='text-xs text-gray-500'>
                      {file.tamanho_bytes && file.tamanho_bytes > 0
                        ? formatFileSize(file.tamanho_bytes)
                        : 'Tamanho não disponível'}{' '}
                      •{new Date(file.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    type='button'
                    onClick={() => downloadFile(file.id, file.nome_original || file.nome_arquivo)}
                    className='p-1 text-gray-400 hover:text-primary-600 transition-colors'
                    title='Baixar arquivo'
                  >
                    <Download className='h-4 w-4' />
                  </button>
                  <button
                    type='button'
                    onClick={() => deleteFile(file.id)}
                    className='p-1 text-gray-400 hover:text-danger-600 transition-colors'
                    title='Deletar arquivo'
                  >
                    <Trash2 className='h-4 w-4' />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
